import useDelayUnmount from 'fitify-ui/src/hooks/useDelayUnmount'
import { useSmartlook } from 'fitify-ui/src/hooks/useSmartlook'
import {
  useFeatureIsOn,
  useGrowthBook,
} from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useEffect, useMemo, useState } from 'react'

import { Loader } from 'components/Loader/Loader'
import {
  StyledContainer,
  TRANSITION_LENGTH_MS,
} from 'components/Loader/Loader.Styled'
import { RouteImagesPrefetch } from 'components/RoutePrefetch/RouteImagesPrefetch'
import { RouteVideosPrefetch } from 'components/RoutePrefetch/RouteVideosPrefetch'
import { useGrowthBookAttributes } from 'hooks/useGrowthBookAttributes'
import { PlanApp, type PlanAppProps } from 'sections/plan/PlanApp'
import { DataContext, useAppState } from 'utils/state'

type Props = PlanAppProps & { isLoaded?: boolean }

export const App = ({ Component, pageProps }: Props) => {
  const { ready } = useGrowthBook()
  const [shouldHideLoader, setShouldHideLoader] = useState(false)
  const { data, set, clear, isLoading } = useAppState()
  const isLoaderVisible = useDelayUnmount(
    !shouldHideLoader,
    TRANSITION_LENGTH_MS
  )
  const shouldRenderLoader = useDelayUnmount(
    isLoaderVisible,
    TRANSITION_LENGTH_MS
  )

  // Handle smartlook
  const isSmartlookEnabled = useFeatureIsOn('smartlook_enabled')
  useSmartlook(isSmartlookEnabled)
  useGrowthBookAttributes()

  useEffect(() => {
    if (ready && !isLoading) {
      setShouldHideLoader(true)
    }
  }, [isLoading, ready])

  const isLoaderMounted =
    !shouldHideLoader || isLoaderVisible || shouldRenderLoader
  const isChildrenVisible = shouldHideLoader

  const dataContextValue = useMemo(
    () => ({ data, set, clear }),
    [data, set, clear]
  )

  return (
    <DataContext.Provider value={dataContextValue}>
      <RouteImagesPrefetch />
      <RouteVideosPrefetch />
      {isLoaderMounted && (
        <StyledContainer $isVisible={!shouldHideLoader || isLoaderVisible}>
          <Loader />
        </StyledContainer>
      )}
      {isChildrenVisible && (
        <PlanApp Component={Component} pageProps={pageProps} />
      )}
    </DataContext.Provider>
  )
}
